/**
 * Switcher
 * タブ切替機能
 *
 * @example
 * HTML:
 * <div class="js-switcher">
 *   <ul>
 *     <li><a href="#tab1" class="js-switcher-trigger">タブ1</a></li>
 *     <li><a href="#tab2" class="js-switcher-trigger">タブ2</a></li>
 *   </ul>
 * </div>
 * <div>
 *   <div id="tab1" class="js-switcher-target">コンテンツ1</div>
 *   <div id="tab2" class="js-switcher-target">コンテンツ2</div>
 * </div>
 *
 * JS: new Switcher();
 */
export default class Switcher {
  constructor(options = {}) {
    this.options = {
      rootSelector: '.js-switcher',
      triggerSelector: '.js-switcher-trigger',
      targetSelector: '.js-switcher-target',
      activeClass: 'is-active',
      fadeDelay: 25,
      fadeDuration: 600,
      slideDistance: 24,
      ...options
    };

    this.switcherElements = [];
    this.validHashes = []; // Switcher用の有効なハッシュリスト

    this.init();
  }

  init() {
    // 全てのSwitcher要素を取得
    const rootElements = document.querySelectorAll(this.options.rootSelector);
    if (rootElements.length === 0) return;

    // 各Switcherを初期化
    rootElements.forEach(rootElement => {
      this.setupSwitcher(rootElement);
    });

    // 有効なハッシュを収集
    this.collectValidHashes();

    // URLハッシュによる初期表示
    this.initFromHash();

    // ハッシュ変更の監視
    window.addEventListener('hashchange', () => this.handleHashChange());
  }

  /**
   * Switcherのセットアップ
   */
  setupSwitcher(rootElement) {
    const triggers = rootElement.querySelectorAll(this.options.triggerSelector);

    if (triggers.length === 0) return;

    // タブリストのrole属性を設定
    const tabList = triggers[0].closest('ul');
    if (tabList) {
      tabList.setAttribute('role', 'tablist');
      tabList.setAttribute('aria-label', 'コンテンツタブ');
    }

    // Switcher要素を記録
    this.switcherElements.push({
      root: rootElement,
      triggers: triggers
    });

    // 各トリガーにイベントとARIA属性を設定
    triggers.forEach((trigger, index) => {
      // トリガーのID生成（なければ）
      if (!trigger.id) {
        const targetHref = trigger.getAttribute('href');
        const targetId = targetHref ? targetHref.substring(1) : '';
        trigger.id = `nav-${targetId || `tab${index + 1}`}`;
      }

      // 親のli要素にrole="none"を設定（ARIAの観点からリスト構造を無効化）
      const parentLi = trigger.parentElement;
      if (parentLi && parentLi.tagName === 'LI') {
        parentLi.setAttribute('role', 'none');
      }

      // ARIA属性を設定
      trigger.setAttribute('role', 'tab');
      trigger.setAttribute('aria-selected', 'false');
      
      const targetHref = trigger.getAttribute('href');
      if (targetHref && targetHref.startsWith('#')) {
        const targetId = targetHref.substring(1);
        trigger.setAttribute('aria-controls', targetId);
        
        // 対応するターゲットにもARIA属性を設定
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.setAttribute('role', 'tabpanel');
          targetElement.setAttribute('aria-labelledby', trigger.id);
          targetElement.setAttribute('tabindex', '0');
        }
      }

      trigger.addEventListener('click', (e) => this.handleTriggerClick(e, rootElement));
    });

    // 初期状態の設定（最初のタブをアクティブに）
    const firstTrigger = triggers[0];
    const firstLi = firstTrigger?.parentElement;
    if (firstLi && !window.location.hash) {
      firstLi.classList.add(this.options.activeClass);
      firstTrigger.setAttribute('aria-selected', 'true');
      this.showTarget(firstTrigger);
    }
  }

  /**
   * 有効なハッシュを収集（Switcher用のみ）
   */
  collectValidHashes() {
    this.validHashes = [];

    // 全てのSwitcher内のトリガーからハッシュを取得
    document.querySelectorAll(`${this.options.rootSelector} ${this.options.triggerSelector}`).forEach(trigger => {
      const hash = trigger.getAttribute('href');
      if (hash && hash.startsWith('#')) {
        this.validHashes.push(hash.substring(1)); // #を除いたIDを保存
      }
    });

    // Switcher targetのIDも収集
    document.querySelectorAll(this.options.targetSelector).forEach(target => {
      if (target.id) {
        this.validHashes.push(target.id);
      }
    });

    // 重複を削除
    this.validHashes = [...new Set(this.validHashes)];
  }

  /**
   * トリガークリック処理
   */
  handleTriggerClick(e, rootElement) {
    e.preventDefault();

    const trigger = e.currentTarget;
    const triggerLi = trigger.parentElement;

    // 同じSwitcher内の全てのトリガーのARIA属性を更新
    const allTriggers = rootElement.querySelectorAll(this.options.triggerSelector);
    allTriggers.forEach(t => {
      t.setAttribute('aria-selected', 'false');
    });

    // クリックされたトリガーのARIA属性を更新
    trigger.setAttribute('aria-selected', 'true');

    // 同じSwitcher内の全てのliからis-activeを削除
    const allLi = rootElement.querySelectorAll('li');
    allLi.forEach(li => li.classList.remove(this.options.activeClass));

    // クリックされたliにis-activeを追加
    if (triggerLi) {
      triggerLi.classList.add(this.options.activeClass);
    }

    // 対応するコンテンツを表示
    this.showTarget(trigger);

    // URLハッシュを更新（履歴に追加）
    const hash = trigger.getAttribute('href') || trigger.getAttribute('data-target');
    if (hash && hash.startsWith('#')) {
      history.pushState(null, null, hash);
    }
  }

  /**
   * ターゲットコンテンツの表示
   */
  showTarget(trigger) {
    const targetId = trigger.getAttribute('href') || trigger.getAttribute('data-target');
    if (!targetId || !targetId.startsWith('#')) return;

    const targetElement = document.querySelector(targetId);
    if (!targetElement) return;

    // 全てのターゲットを非表示
    const allTargets = document.querySelectorAll(this.options.targetSelector);
    allTargets.forEach(target => {
      target.classList.remove(this.options.activeClass);
      target.style.display = 'none';
    });

    // ターゲットを表示
    targetElement.style.display = 'block';
    targetElement.classList.add(this.options.activeClass);

    // フェードインアニメーション
    this.fadeInTarget(targetElement);

    // スクロールアニメーションの再適用
    this.reapplyScrollAnimation(targetElement);
  }

  /**
   * フェードインアニメーション
   */
  fadeInTarget(targetElement) {
    // 直接の子要素を取得
    const children = Array.from(targetElement.children);

    if (children.length === 0) {
      // 子要素がない場合は要素自体をアニメーション
      this.fadeInElements([targetElement]);
    } else {
      // 子要素を順番にアニメーション
      this.fadeInElements(children);
    }
  }

  /**
   * 要素のフェードインアニメーション
   */
  fadeInElements(elements) {
    elements.forEach((element, index) => {
      // 初期状態
      element.style.opacity = '0';
      element.style.transform = `translateY(${this.options.slideDistance}px)`;
      element.style.transition =
        `opacity ${this.options.fadeDuration}ms cubic-bezier(0.4,0,0.2,1), ` +
        `transform ${this.options.fadeDuration}ms cubic-bezier(0.4,0,0.2,1)`;

      // 遅延して表示
      setTimeout(() => {
        requestAnimationFrame(() => {
          element.style.opacity = '1';
          element.style.transform = 'translateY(0)';
        });
      }, index * this.options.fadeDelay);
    });
  }

  /**
   * スクロールアニメーションの再適用
   */
  reapplyScrollAnimation(scope) {
    const scrollAnimTargets = scope.querySelectorAll('.js-scroll-anim');
    if (scrollAnimTargets.length === 0) return;

    // is-animatedクラスをリセット
    scrollAnimTargets.forEach(target => {
      target.classList.remove('is-animated');
    });

    // ScrollAnimationモジュールのobserverがあれば再監視
    if (window.scrollAnimationInstance) {
      scrollAnimTargets.forEach(target => {
        window.scrollAnimationInstance.observer.observe(target);
      });
    }

    // 既に表示領域にある要素は即座にアニメーション
    setTimeout(() => {
      scrollAnimTargets.forEach(target => {
        const rect = target.getBoundingClientRect();
        const isVisible = rect.top <= window.innerHeight && rect.bottom >= 0;

        if (isVisible) {
          target.classList.add('is-animated');
        }
      });
    }, 100);
  }

  /**
   * URLハッシュによる初期表示
   */
  initFromHash() {
    const hash = window.location.hash;
    if (!hash) return;

    const hashId = hash.substring(1);

    // 1. Switcherトリガー用のハッシュかチェック
    if (this.validHashes.includes(hashId)) {
      // 該当するトリガーを探す
      const matchedTrigger = document.querySelector(
        `${this.options.rootSelector} ${this.options.triggerSelector}[href="${hash}"]`
      );

      if (matchedTrigger) {
        // トリガーの親liをアクティブにする
        const triggerLi = matchedTrigger.parentElement;
        const rootElement = matchedTrigger.closest(this.options.rootSelector);

        this.activateTrigger(triggerLi, rootElement, matchedTrigger);
      }
      return;
    }

    // 2. Switcherターゲット内の要素かチェック
    const targetElement = document.getElementById(hashId);
    if (targetElement) {
      // 要素の親にSwitcherターゲットがあるかチェック
      const switcherTarget = targetElement.closest(this.options.targetSelector);
      if (switcherTarget) {
        const targetId = switcherTarget.id;

        // 対応するトリガーを探す
        const correspondingTrigger = document.querySelector(
          `${this.options.rootSelector} ${this.options.triggerSelector}[href="#${targetId}"]`
        );

        if (correspondingTrigger) {
          const triggerLi = correspondingTrigger.parentElement;
          const rootElement = correspondingTrigger.closest(this.options.rootSelector);

          this.activateTrigger(triggerLi, rootElement, correspondingTrigger);
        }
      }
    }
  }

  activateTrigger(triggerLi, rootElement, trigger) {
    if (triggerLi && rootElement) {
      // 同じSwitcher内の全てのトリガーのARIA属性を更新
      const allTriggers = rootElement.querySelectorAll(this.options.triggerSelector);
      allTriggers.forEach(t => {
        t.setAttribute('aria-selected', 'false');
      });

      // 該当のトリガーのARIA属性を更新
      trigger.setAttribute('aria-selected', 'true');

      // 同じSwitcher内のliからis-activeを削除
      const allLi = rootElement.querySelectorAll('li');
      allLi.forEach(li => li.classList.remove(this.options.activeClass));

      // 該当のliにis-activeを追加
      triggerLi.classList.add(this.options.activeClass);

      // コンテンツを表示
      this.showTarget(trigger);
    }
  }

  /**
   * ハッシュ変更処理
   */
  handleHashChange() {
    this.initFromHash();
  }

  /**
   * インスタンスの破棄
   */
  destroy() {
    // イベントリスナーの削除
    window.removeEventListener('hashchange', () => this.handleHashChange());

    // 全てのトリガーからイベントを削除
    this.switcherElements.forEach(({triggers}) => {
      triggers.forEach(trigger => {
        trigger.removeEventListener('click', this.handleTriggerClick);
      });
    });

    this.switcherElements = [];
    this.validHashes = [];
  }
}
